import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import ApplyWizard from "@/components/site/ApplyWizard";
import {
    Sparkle, ShieldCheck, ArrowLeft, WarningCircle, CheckCircle,
} from "@phosphor-icons/react";

/* ===========================================================================
   ApplyAsProvider — Path B Phase 2 (2026-02-25)
   ---------------------------------------------------------------------------
   The landing page hit when a lead clicks the magic-link in a Sales rep's
   invitation email. Resolves `/api/provider-apply/by-token/:token`, prefills
   the ApplyWizard with the lead's sales-verified identity (clinic, name,
   email, phone, city) and locks those fields so they can't be edited
   mid-flow. The token + b2b_lead_id are sent with the wizard's submit so the
   backend can flip the lead → "applied" automatically.
   =========================================================================== */

const STATES = {
    LOADING: "loading",
    READY: "ready",
    EXPIRED: "expired",
    ALREADY_APPLIED: "already_applied",
    NOT_FOUND: "not_found",
};

const LOCKED_FIELDS = new Set(["email", "phone"]);


export default function ApplyAsProvider() {
    const { token } = useParams();
    const navigate = useNavigate();
    const [state, setState] = useState(STATES.LOADING);
    const [prefill, setPrefill] = useState(null);
    const [errorDetail, setErrorDetail] = useState("");
    const [submittedId, setSubmittedId] = useState(null);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const r = await api.get(`/provider-apply/by-token/${token}`);
                if (!alive) return;
                if (r.data.already_applied) {
                    setPrefill(r.data);
                    setState(STATES.ALREADY_APPLIED);
                } else {
                    setPrefill(r.data);
                    setState(STATES.READY);
                }
            } catch (err) {
                if (!alive) return;
                const status = err.response?.status;
                setErrorDetail(err.response?.data?.detail || "");
                if (status === 410) setState(STATES.EXPIRED);
                else setState(STATES.NOT_FOUND);
            }
        })();
        return () => { alive = false; };
    }, [token]);

    return (
        <div className="min-h-screen bg-secondary/30 relative overflow-hidden" data-testid="apply-as-provider-page">
            {/* Faint blue-grid background — same texture as the Landing hero */}
            <div className="absolute inset-0 blue-grid-bg opacity-50 mask-fade-b pointer-events-none" aria-hidden="true" />
            <div className="absolute -top-32 -left-20 w-80 h-80 rounded-full bg-primary/10 blur-3xl pointer-events-none" aria-hidden="true" />
            <div className="absolute -bottom-32 -right-20 w-80 h-80 rounded-full bg-amber-200/30 blur-3xl pointer-events-none" aria-hidden="true" />

            <div className="relative max-w-3xl mx-auto px-5 sm:px-8 py-12 lg:py-16">
                <Link to="/" className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-primary mb-6 transition-colors" data-testid="back-home-link">
                    <ArrowLeft size={13} weight="bold" /> Back to home
                </Link>

                <div className="bg-card rounded-3xl border border-border/70 bento-shadow p-7 sm:p-10" data-testid="apply-token-card">
                    {state === STATES.LOADING && <Loading />}
                    {state === STATES.NOT_FOUND && <ErrorState detail={errorDetail} onHome={() => navigate("/")} />}
                    {state === STATES.EXPIRED && <ExpiredState detail={errorDetail} />}
                    {state === STATES.ALREADY_APPLIED && <AlreadyAppliedState prefill={prefill} />}
                    {state === STATES.READY && (
                        <ReadyState prefill={prefill} token={token} submittedId={submittedId} onSuccess={setSubmittedId} />
                    )}
                </div>
            </div>
        </div>
    );
}


/* =========================================================================== */

function Loading() {
    return (
        <div className="text-center py-12" data-testid="apply-token-loading">
            <div className="w-12 h-12 mx-auto rounded-full border-4 border-primary/30 border-t-primary animate-spin mb-5" />
            <p className="text-sm text-muted-foreground">Looking up your invitation…</p>
        </div>
    );
}

function ErrorState({ detail, onHome }) {
    return (
        <div className="text-center py-8" data-testid="apply-token-not-found">
            <div className="w-14 h-14 mx-auto rounded-full bg-rose-100 grid place-items-center mb-5">
                <WarningCircle size={28} weight="duotone" className="text-rose-600" />
            </div>
            <h2 className="font-display text-2xl font-bold mb-2">This invitation link is no longer valid.</h2>
            <p className="text-sm text-muted-foreground mb-2">
                {detail || "We couldn't find an invitation matching this link."}
            </p>
            <p className="text-sm text-muted-foreground mb-6">
                If you believe this is a mistake, please reply to the email from your Invisique™ partnership rep,
                or apply directly without a referral.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
                <Button onClick={onHome} variant="outline" className="rounded-full">Back to home</Button>
                <Link to="/for-doctors#apply">
                    <Button className="rounded-full">Apply directly instead</Button>
                </Link>
            </div>
        </div>
    );
}

function ExpiredState({ detail }) {
    return (
        <div className="text-center py-8" data-testid="apply-token-expired">
            <div className="w-14 h-14 mx-auto rounded-full bg-amber-100 grid place-items-center mb-5">
                <WarningCircle size={28} weight="duotone" className="text-amber-600" />
            </div>
            <h2 className="font-display text-2xl font-bold mb-2">This invitation has expired.</h2>
            <p className="text-sm text-muted-foreground mb-6">
                {detail || "For security, partner invitations expire after 14 days. Reply to your Invisique™ partnership rep's email to receive a fresh link."}
            </p>
            <Link to="/for-doctors#apply">
                <Button className="rounded-full">Apply directly instead</Button>
            </Link>
        </div>
    );
}

function AlreadyAppliedState({ prefill }) {
    return (
        <div className="text-center py-8" data-testid="apply-token-already-applied">
            <div className="w-14 h-14 mx-auto rounded-full bg-primary/10 grid place-items-center mb-5">
                <CheckCircle size={28} weight="duotone" className="text-primary" />
            </div>
            <h2 className="font-display text-2xl font-bold mb-2">You've already submitted this application.</h2>
            <p className="text-sm text-muted-foreground mb-2">
                <strong>{prefill.clinic}</strong> — submitted under <strong>{prefill.contact_name}</strong>.
            </p>
            <p className="text-sm text-muted-foreground mb-6">
                Our partnership team is reviewing your application. You'll receive an email at <strong>{prefill.email}</strong> with the decision within 3 business days.
            </p>
            <Link to="/for-doctors#status">
                <Button variant="outline" className="rounded-full">Check application status</Button>
            </Link>
        </div>
    );
}

function ReadyState({ prefill, token, submittedId, onSuccess }) {
    if (submittedId) {
        // success view is owned by ApplyWizard, but we add a parent ribbon when
        // the submission came in via a magic-link so the lead knows their
        // sales rep has been notified.
        return null;
    }
    return (
        <>
            {/* Trust ribbon — reassures the lead this is the right invite */}
            <div className="rounded-2xl bg-primary/8 border border-primary/20 px-5 py-4 mb-7 flex items-start gap-3" data-testid="apply-token-ribbon">
                <div className="w-9 h-9 rounded-full bg-primary text-white grid place-items-center shrink-0 ring-4 ring-secondary/40">
                    <ShieldCheck size={16} weight="fill" />
                </div>
                <div className="text-sm leading-snug">
                    <div className="font-semibold mb-0.5">
                        <span className="overline text-primary mr-1.5 inline-flex items-center gap-1"><Sparkle size={10} weight="fill" /> Invited</span>
                        Welcome, {prefill.contact_name || "Doctor"}.
                    </div>
                    <div className="text-muted-foreground text-xs">
                        Your Invisique™ partnership rep has invited <strong className="text-foreground">{prefill.clinic}</strong> to complete the full application.
                        Email + phone are locked to the contact you've already verified.
                    </div>
                </div>
            </div>

            <ApplyWizard
                initial={{
                    clinic: prefill.clinic || "",
                    name: prefill.contact_name || "",
                    email: prefill.email || "",
                    phone: prefill.phone || "",
                    city: prefill.city || "",
                    cases_per_month: prefill.cases_per_month || "",
                    current_aligner_brand: prefill.current_aligner_brand || "",
                }}
                lockedFields={LOCKED_FIELDS}
                inviteToken={token}
                b2bLeadId={prefill.lead_id}
                onSuccess={onSuccess}
            />
        </>
    );
}
